import React from "react";
import PropTypes from "prop-types";
import {
  Snackbar,
  SnackbarContent,
  IconButton,
  Typography,
  makeStyles,
} from "@material-ui/core";
// Import new icons
import CheckIcon from "@material-ui/icons/Check";
import ClearIcon from "@material-ui/icons/Clear";
import CloseIcon from "@material-ui/icons/Close";
import InfoIcon from "@material-ui/icons/Info";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import {
  SuccessSnackbarIcon,
  WarningSnackbarIcon,
} from "../../assets/icons/SnackbarIcon";

const useStyles = makeStyles((theme) => ({
  // ... (styles remain the same)
  snackbarContent: {
    backgroundColor: "white",
    color: "#1e1e1e",
    boxShadow: theme.shadows[4],
    borderRadius: theme.spacing(1),
    padding: 0,
    overflow: "hidden",
    width: 400,
    height: 150,
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  svgBackground: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 120,
    height: "100%",
    zIndex: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  iconWrapper: {
    position: "absolute",
    top: 16,
    left: 16,
    borderRadius: "10%",
    padding: "6px 8px",
    boxShadow: "0 0 6px rgb(14 14 14)",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  textContent: {
    marginLeft: 60,
    marginTop: 15,
    padding: theme.spacing(2),
    zIndex: 1,
  },
  closeButton: {
    position: "absolute",
    top: 4,
    right: 4,
    zIndex: 2,
    color: theme.palette.grey[600],
  },
}));

const CustomSnackbar = ({
  open,
  onClose,
  title,
  subtitle,
  type,
  autoClose,
  autoCloseDuration,
}) => {
  const classes = useStyles();
  if(!type) return null

  // Using a switch statement for better readability
  const getIcon = () => {
    const iconStyle = { color: "white", fontSize: 25, fontWeight: 700 };
    switch (type) {
      case "success":
        return <CheckIcon style={iconStyle} />;
      case "warning":
        return <ClearIcon style={iconStyle} />;
      case "info":
        return <InfoIcon style={iconStyle} />;
      case "error":
        return <ReportProblemIcon style={iconStyle} />;
      default:
        return <CheckIcon style={iconStyle} />;
    }
  };

  const getColor = () => {
    switch (type) {
      case "success":
        return "#0ba404";
      case "warning":
        return "#ff9800"; // Amber for warning
      case "info":
        return "#2196f3"; // Blue for info
      case "error":
        return "#ef210c"; // Red for error
      default:
        return "#0ba404";
    }
  };

  const getBackgroundIcon = () => {
      switch (type) {
          case "success":
              return <SuccessSnackbarIcon />;
          case "warning":
          case "error": // Re-using warning icon for error for now
              return <WarningSnackbarIcon />;
          case "info":
              return <SuccessSnackbarIcon />; // Re-using success icon for info
          default:
              return <SuccessSnackbarIcon />;
      }
  }

  return (
    <Snackbar
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      open={open}
      onClose={onClose}
      autoHideDuration={autoClose ? autoCloseDuration : null}
    >
      <SnackbarContent
        className={classes.snackbarContent}
        message={
          <>
            <div className={classes.svgBackground}>
              {getBackgroundIcon()}
            </div>
            <div
              className={classes.iconWrapper}
              style={{ backgroundColor: getColor() }}
            >
              {getIcon()}
            </div>
            <div className={classes.textContent}>
              <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                {title}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            </div>
          </>
        }
        action={
          <IconButton onClick={onClose} className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        }
      />
    </Snackbar>
  );
};

CustomSnackbar.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["success", "warning", "info", "error"]).isRequired,
  autoClose: PropTypes.bool,
  autoCloseDuration: PropTypes.number,
};

CustomSnackbar.defaultProps = {
  autoClose: true,
  autoCloseDuration: 5000,
};


export default CustomSnackbar;
